import { apiSlice } from "./http";
import { Tags } from "../enum/tags";
import { Paginated } from "@/@types/paginated";
import {
  IRegionalSubstitution,
  IRegionalSubstitutionParams,
  ICreateRegionalSubstitution,
  IUpdateRegionalSubstitution,
  ISubstituteCandidatesParams,
  IGerenteSummary,
} from "@/@types/regional-substitution";

export const regionalSubstitutionService = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getRegionalSubstitutions: builder.query<
      Paginated<IRegionalSubstitution>,
      IRegionalSubstitutionParams
    >({
      query: ({ meta, search, status, orderBy }) => {
        const perPage = meta.perPage ?? 10;
        const params = new URLSearchParams({
          page: meta.page.toString(),
          perPage: perPage.toString(),
        });

        if (search) params.append("search", search);
        if (status) params.append("status", status);
        if (orderBy) params.append("orderBy", orderBy);

        return {
          url: `/regional-substitution?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: [{ type: Tags.REGIONAL_SUBSTITUTION, id: "LIST" }],
    }),

    getRegionalSubstitutionById: builder.query<IRegionalSubstitution, string>({
      query: (id) => ({
        url: `/regional-substitution/${id}`,
        method: "GET",
      }),
      providesTags: (_result, _error, id) => [
        { type: Tags.REGIONAL_SUBSTITUTION, id },
      ],
    }),

    createRegionalSubstitution: builder.mutation<
      string,
      ICreateRegionalSubstitution
    >({
      query: (data) => ({
        url: "/regional-substitution",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [{ type: Tags.REGIONAL_SUBSTITUTION, id: "LIST" }],
    }),

    updateRegionalSubstitution: builder.mutation<
      string,
      { id: string; data: IUpdateRegionalSubstitution }
    >({
      query: ({ id, data }) => ({
        url: `/regional-substitution/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: Tags.REGIONAL_SUBSTITUTION, id },
        { type: Tags.REGIONAL_SUBSTITUTION, id: "LIST" },
      ],
    }),

    cancelRegionalSubstitution: builder.mutation<
      string,
      { id: string; version: number }
    >({
      query: ({ id, version }) => ({
        url: `/regional-substitution/${id}/cancel`,
        method: "PATCH",
        body: { version },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: Tags.REGIONAL_SUBSTITUTION, id },
        { type: Tags.REGIONAL_SUBSTITUTION, id: "LIST" },
      ],
    }),

    substituteCandidates: builder.query<
      Paginated<IGerenteSummary>,
      ISubstituteCandidatesParams
    >({
      query: ({ search, orderBy, perPage, meta }) => {
        const params = new URLSearchParams({
          orderBy,
          page: meta.page.toString(),
          perPage: perPage.toString(),
        });

        if (search) params.append("search", search);

        return {
          url: `/regional-substitution/substitute-candidates?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: [{ type: Tags.REGIONAL_SUBSTITUTION, id: "CANDIDATES" }],
    }),
  }),
  overrideExisting: true,
});

export const {
  useGetRegionalSubstitutionsQuery,
  useGetRegionalSubstitutionByIdQuery,
  useCreateRegionalSubstitutionMutation,
  useUpdateRegionalSubstitutionMutation,
  useCancelRegionalSubstitutionMutation,
  useSubstituteCandidatesQuery,
} = regionalSubstitutionService;
